// Configuración → Procesos (client-ops §3, PR 17). GET/POST/PATCH/DELETE
// /settings/pipelines and its /stages children, manage-only.
//
// Every mutation returns the WHOLE board, not the row it touched: making a
// funnel the default un-defaults another one, deleting a stage renumbers its
// siblings, and moving a marker clears it somewhere else. A client that patched
// one row into its cache would be showing two defaults until the next refetch.

import { type AgentTerminal, captureWindowOf } from '@vysto/shared/compiler'

import type { FieldDataType } from './fields-data.js'
import type { AssignableMemberView } from './settings-data.js'
import { apiClient } from '../../api/client.js'

/** What a stage MEANS. The only property any code may branch on — stage keys
 *  and names are the tenant's own words. */
export type StageKind = 'open' | 'won' | 'lost'

/** Who is expected to act while a prospect sits in the stage. 'bot' = the
 *  Agente keeps working it; 'team' / 'member' hand the conversation to people. */
export type StageOwnerType = 'bot' | 'team' | 'member'

/** How a stage the TEAM attends picks its person, when it does not attach a
 *  named policy. 'none' = nobody automatically (the default; a bare null means
 *  the same thing); the team claims by hand. */
export type StageTeamPolicy = 'balanced' | 'none'

/** Lo que CORRE mientras una conversación está en la etapa (W1-1). Espejo del
 *  conjunto cerrado que la base de datos impone. Desde F2-4 la pantalla lo
 *  MUESTRA en cada fila y edita tres de los seis; los otros tres se derivan o
 *  todavía no tienen emisor. */
export type StageBehavior =
  | 'inicio'
  | 'captura'
  /** El Agente AGENDA la cita en esta etapa (D22-1). Desde D22-9 también se
   *  ELIGE: el compilador emite el tramo de reserva y el API la acepta. */
  | 'agenda'
  | 'multiplexor'
  | 'llenado_manual'
  | 'handoff'
  | 'fin'

/** Los que el cliente puede ESCRIBIR hoy (F2-4, y `agenda` desde D22-9), y que
 *  por tanto son los únicos que el diálogo de etapa manda. `fin` lo pone el tipo
 *  de etapa (ganada / perdida) y el servidor lo deriva; `inicio` y `multiplexor`
 *  son legales en la base pero no tienen emisor todavía, y el API los rechaza
 *  diciendo justo eso. */
export type AuthoredStageBehavior = 'captura' | 'llenado_manual' | 'handoff' | 'agenda'

/** Quién ve los prospectos de UN proceso (F18): 'all' = todo el equipo;
 *  'assigned' = cada asesor ve solo los suyos y los sin asignar (Dueño y
 *  Administrador siempre ven todo). Las conversaciones siguen la
 *  configuración del espacio en Operación, aparte. */
export type PipelineVisibilityPolicy = 'all' | 'assigned'

export interface PipelineStageView {
  id: string
  key: string
  name: string
  position: number
  kind: StageKind
  ownerType: StageOwnerType
  ownerMemberId: string | null
  /** 'balanced' | 'none' | null — null significa lo mismo que 'none'. */
  teamPolicy: StageTeamPolicy | null
  /** La política nombrada que la etapa tiene puesta, o null. */
  teamPolicyId: string | null
  requiredFields: string[]
  /** Los datos que ESTA etapa se encarga de conseguir (CP3 L). No es un
   *  requisito: no permite ni niega un movimiento. Ordena la ficha del
   *  prospecto, cuenta la historia del proceso y, desde F3-1, decide qué
   *  PREGUNTA la captura compilada en esta etapa. */
  gatheredFields: string[]
  isCommitment: boolean
  /** Lo que corre en esta columna. Lo lee la vista previa del compilador y,
   *  desde F2-4, la fila de la etapa: es lo que hace que la lista se lea como
   *  un proceso y no como una lista de nombres. */
  behavior: StageBehavior
  /** Prospects in this column. Zero is what makes deleting it possible. */
  leadCount: number
}

/** Un dato de la FICHA de un proceso (CP3 K): una definición de la BIBLIOTECA
 *  del espacio que ESTE proceso lleva, en el orden de la ficha.
 *  El `id` es lo que viaja al guardar; la clave es para leer. */
export interface PipelineFieldView {
  id: string
  key: string
  label: string
  dataType: FieldDataType
  options: string[] | null
  /** Cómo pregunta el Agente ESTE dato en ESTE proceso (F4-4). `null` = usa la
   *  frase generada, que sigue la etiqueta de la biblioteca: la pantalla la
   *  enseña como placeholder en vez de escribirla dentro del campo, para que
   *  renombrar el dato la siga moviendo sola. */
  questionPrompt: string | null
}

export interface PipelineView {
  id: string
  key: string
  name: string
  position: number
  isDefault: boolean
  /** El muro de visibilidad de ESTE proceso — per-proceso desde F18. */
  visibilityPolicy: PipelineVisibilityPolicy
  /** Cómo reconoce el Agente ESTE proceso (F4-5): la frase con la que lo ofrece
   *  en su menú. `null` = nadie escribió una y el agente usa la DERIVADA del
   *  nombre, que la pantalla enseña como placeholder en vez de escribirla dentro
   *  del campo, para que renombrar el proceso la siga moviendo sola. */
  entryLabel: string | null
  /** QUÉ SIGNIFICA este proceso (F5-2): con qué lo RECONOCE el agente cuando la
   *  persona lo pide con otras palabras, que no es lo mismo que la frase con la
   *  que lo ofrece. `null` = nadie escribió uno y el agente lo reconoce por su
   *  etiqueta, que es lo que hacía antes. */
  meaning: string | null
  /** Los datos que llevan los prospectos de este proceso, en orden (CP3 K).
   *  Vacío es un estado real: un proceso recién creado no lleva ninguno. */
  fields: PipelineFieldView[]
  stages: PipelineStageView[]
  leadCount: number
}

export interface PipelinesSettings {
  pipelines: PipelineView[]
  /** Who a stage may name as its owner — the same roster the assignment rules
   *  are checked against. */
  members: AssignableMemberView[]
  /** The tenant's named assignment policies (id + name), so the stage modal
   *  offers one option per policy — and a hint pointing at Operación when
   *  there are none. */
  policies: { id: string; name: string }[]
}

export async function getPipelines(): Promise<PipelinesSettings> {
  const res = await apiClient.get<{ data: PipelinesSettings }>('/settings/pipelines')
  return res.data.data
}

/** Create a funnel. The server slugs the key from the name and seeds three
 *  stages, so what comes back is usable rather than an empty board. */
export async function createPipeline(name: string): Promise<PipelinesSettings> {
  const res = await apiClient.post<{ data: PipelinesSettings }>('/settings/pipelines', { name })
  return res.data.data
}

/** Rename a funnel, make it the default (which un-defaults the previous one
 *  in the same transaction), or set who sees its leads. */
export async function updatePipeline(
  id: string,
  edit: {
    name?: string
    isDefault?: true
    visibilityPolicy?: PipelineVisibilityPolicy
    /** Cómo lo reconoce el agente (F4-5). La cadena VACÍA borra la frase y
     *  devuelve al agente a la derivada del nombre: es un valor con significado,
     *  no un campo ausente, así que se manda. */
    entryLabel?: string
    /** Qué significa el proceso (F5-2). La cadena VACÍA lo borra y devuelve al
     *  agente a reconocerlo por su etiqueta: es un valor con significado, no un
     *  campo ausente, así que se manda. */
    meaning?: string
  },
): Promise<PipelinesSettings> {
  const res = await apiClient.patch<{ data: PipelinesSettings }>(
    `/settings/pipelines/${encodeURIComponent(id)}`,
    edit,
  )
  return res.data.data
}

export async function deletePipeline(id: string): Promise<PipelinesSettings> {
  const res = await apiClient.delete<{ data: PipelinesSettings }>(
    `/settings/pipelines/${encodeURIComponent(id)}`,
  )
  return res.data.data
}

export async function createStage(
  pipelineId: string,
  input: {
    name: string
    kind: StageKind
    behavior?: AuthoredStageBehavior
    /** QUIÉN atiende la etapa desde que nace (F5-3). Ausente = el Agente, que
     *  es como nace toda etapa desde este diálogo. Una etapa de TRASPASO nace
     *  del equipo: su razón de ser es entregar la conversación a una persona,
     *  y nacida del Agente la fila decía "Responsable: El Agente" justo debajo
     *  de "Pasa con una persona". No hay 'member': nombrar a alguien exige la
     *  comprobación de plantilla que vive en el PATCH de la etapa. */
    ownerType?: 'bot' | 'team'
    /** DÓNDE nace la etapa, en el orden del proceso (F4-6). Sin esto se queda
     *  al final, que para una etapa de traspaso es después de Ganada y Perdida:
     *  la conversación ya terminó ahí, así que el final no cambiaría y el botón
     *  sería una puerta pintada. El servidor la crea y la mueve en la misma
     *  transacción (`createStage` → `moveStageTo`). */
    position?: number
  },
): Promise<PipelinesSettings> {
  const res = await apiClient.post<{ data: PipelinesSettings }>(
    `/settings/pipelines/${encodeURIComponent(pipelineId)}/stages`,
    input,
  )
  return res.data.data
}

export interface StageEdit {
  name?: string
  kind?: StageKind
  position?: number
  ownerType?: StageOwnerType
  ownerMemberId?: string | null
  /** 'balanced' | 'none'; null clears it. Sent only for a 'team' stage; the
   *  API refuses it on the other two, the same way the database does. XOR with
   *  teamPolicyId — setting one clears the other server-side. */
  teamPolicy?: StageTeamPolicy | null
  /** The named policy to attach, or null to detach. */
  teamPolicyId?: string | null
  requiredFields?: string[]
  /** Lo que la etapa OBTIENE. Es narrativo: no exige nada (CP3 L). */
  gatheredFields?: string[]
  isCommitment?: boolean
  /** Qué pasa en la etapa (F2-4). Solo viaja en las etapas abiertas: en una
   *  ganada o perdida el final lo marca el tipo, y el servidor lo deriva. */
  behavior?: AuthoredStageBehavior
}

export async function updateStage(
  pipelineId: string,
  stageId: string,
  edit: StageEdit,
): Promise<PipelinesSettings> {
  const res = await apiClient.patch<{ data: PipelinesSettings }>(
    `/settings/pipelines/${encodeURIComponent(pipelineId)}/stages/${encodeURIComponent(stageId)}`,
    edit,
  )
  return res.data.data
}

export async function deleteStage(pipelineId: string, stageId: string): Promise<PipelinesSettings> {
  const res = await apiClient.delete<{ data: PipelinesSettings }>(
    `/settings/pipelines/${encodeURIComponent(pipelineId)}/stages/${encodeURIComponent(stageId)}`,
  )
  return res.data.data
}

/** Una etapa que perdió un dato requerido al quitarlo de la ficha, con las
 *  ETIQUETAS de los datos que dejó de exigir. Lo devuelve el guardado de la
 *  ficha para que la pantalla diga lo que de verdad cambió, no solo lo que
 *  advirtió antes de guardar. */
export interface StrippedStage {
  key: string
  name: string
  labels: string[]
}

export interface PipelineFieldsResult extends PipelinesSettings {
  stripped: StrippedStage[]
  /** La ETIQUETA del dato de interés que el servidor sentó solo, cuando la
   *  ficha guardada no lo llevaba. Llega únicamente cuando eso pasó: la
   *  pantalla no puede saberlo por su cuenta (el guardado manda ids, no reglas
   *  de plataforma) y agregar un dato que nadie pidió sin decirlo se lee como
   *  un error nuestro. */
  seatedInterest?: string
}

interface FieldsResultPayload extends PipelinesSettings {
  stripped: StrippedStage[]
  seated_interest?: string
}

/** Un dato de la ficha, como viaja al guardar: qué dato, y cómo se pregunta
 *  aquí (F4-4). `questionPrompt` en blanco u omitido = usa la frase generada. */
export interface PipelineFieldSave {
  id: string
  questionPrompt?: string
}

/** PUT /settings/pipelines/:id/fields — reemplaza la FICHA completa del proceso,
 *  en el orden dado. La lista entera cada vez: la pantalla edita una lista y
 *  guarda una lista, y desde F4-4 esa lista lleva también la REDACCIÓN de cada
 *  pregunta, porque es la misma edición en la misma pantalla. */
export async function updatePipelineFields(
  pipelineId: string,
  fields: readonly PipelineFieldSave[],
): Promise<PipelineFieldsResult> {
  const res = await apiClient.put<{ data: FieldsResultPayload }>(
    `/settings/pipelines/${encodeURIComponent(pipelineId)}/fields`,
    { fields },
  )
  const { seated_interest: seatedInterest, ...rest } = res.data.data
  return seatedInterest === undefined ? rest : { ...rest, seatedInterest }
}

/** Las etapas de ESTE proceso que perderían un dato si se quita de la ficha: la
 *  advertencia que el diálogo de confirmación dice ANTES de guardar, con los
 *  mismos datos con los que el API hace el recorte.
 *
 *  Mira lo que la etapa EXIGE y lo que OBTIENE, no solo lo exigido: la captura
 *  compilada arma sus preguntas con `gatheredFields`, así que quitar el dato de
 *  la ficha también le calla la pregunta a la etapa. */
export function stagesRequiring(
  stages: PipelineStageView[],
  fieldKey: string,
): PipelineStageView[] {
  return stages.filter(
    (s) => s.requiredFields.includes(fieldKey) || s.gatheredFields.includes(fieldKey),
  )
}

/** CÓMO TERMINA EL PROCESO, leído de sus etapas (F4-6).
 *
 *  No hay columna nueva ni preferencia guardada: el final YA estaba escrito en
 *  las etapas, y hasta ahora nadie lo decía en pantalla. La cuenta es la MISMA
 *  que emite el agente (`captureWindowOf` de `@vysto/shared/compiler`), no una
 *  copia suya: una segunda regla aquí dejaría la pantalla prometiendo un final
 *  que el agente no corre, que es justo lo que este ciclo viene a terminar. */
export interface PipelineEndingView {
  /** 'pasar' = pasa con una persona; 'cierre' = se despide; 'fin_ok' = el
   *  agente se queda contestando. */
  terminal: AgentTerminal
  /** La etapa de traspaso que DECIDE el final, cuando el final es pasar con una
   *  persona. Es la que el botón de quitar elimina, y la que la frase nombra. */
  handoffStage: PipelineStageView | null
  /** DÓNDE nace una etapa de traspaso nueva: justo después de la última columna
   *  que la captura recorre. Al final de la lista quedaría después de Ganada y
   *  Perdida, donde ya no cambia nada. */
  insertPosition: number
  /** Una etapa DENTRO de la ventana de captura que ya atiende una persona: al
   *  llegar ahí la conversación pasa a manos humanas antes de que la captura
   *  termine (`capture-lead.ts`, `stagedHandoff`). Es la misma advertencia que
   *  da la vista previa del agente, dicha aquí porque aquí se decide el final. */
  midWindowStage: PipelineStageView | null
  /** El agente no captura NADA en este proceso: ninguna de sus columnas compila
   *  (F4-10). No hay entonces "cuando el agente termina de capturar", y tampoco
   *  hay dónde nacería una etapa de traspaso: la posición calculada sería 0, o
   *  sea la PRIMERA columna del tablero. La pantalla lo dice y no ofrece nada. */
  capturesNothing: boolean
  /** Una etapa de traspaso que quedó DESPUÉS de la columna que decide el final
   *  (F4-10). El agente no la alcanza — la terminal se lee en la primera `fin` —
   *  y la pantalla ofrecía "Agregar etapa de traspaso" sobre una columna de
   *  traspaso que ya existe: crearla contestaba 409 nombrando una etapa que el
   *  cliente nunca vio. Se nombra para poder decir que se mueva. */
  strandedHandoffStage: PipelineStageView | null
}

/** El orden TOTAL del proceso: `position`, y la clave para desempatar. El mismo
 *  criterio del compilador, o la posición calculada aquí caería en otro lugar. */
function byStageOrder(a: PipelineStageView, b: PipelineStageView): number {
  return a.position - b.position || (a.key < b.key ? -1 : a.key > b.key ? 1 : 0)
}

export function readPipelineEnding(
  stages: readonly PipelineStageView[],
  fields: readonly PipelineFieldView[],
): PipelineEndingView {
  // Las vistas de etapa y de dato YA tienen la forma que el compilador lee, así
  // que se pasan tal cual: mapearlas a otro tipo sería copiar la regla.
  const ficha = fields.map((f) => ({
    key: f.key,
    label: f.label,
    dataType: f.dataType,
    options: f.options,
  }))
  const window = captureWindowOf(stages, ficha)
  const ordered = [...stages].sort(byStageOrder)
  const last = window.compiledStages[window.compiledStages.length - 1]
  const insertPosition = last === undefined ? 0 : ordered.indexOf(last) + 1
  const after = ordered.slice(insertPosition)
  const handoffAfter = after.find((s) => s.behavior === 'handoff') ?? null
  return {
    terminal: window.terminal,
    // Sólo cuando ella es la que decide: con una columna que cierra antes, el
    // traspaso que viene después no es el final del proceso.
    handoffStage: window.terminal === 'pasar' ? handoffAfter : null,
    insertPosition,
    midWindowStage: window.compiledStages.find((s) => s.ownerType !== 'bot') ?? null,
    capturesNothing: window.compiledStages.length === 0,
    // La misma columna, leída al revés: existe, está después del recorrido, y el
    // final NO es pasar con una persona porque algo la adelantó. Ésa es la que
    // hay que mover, y la única razón por la que el botón de agregar sobraba.
    strandedHandoffStage: window.terminal === 'pasar' ? null : handoffAfter,
  }
}

/** Quién se hace responsable de un prospecto en una etapa, en UNA frase.
 *
 *  Vive junto a los tipos, y no dentro de la pantalla que la inventó, porque el
 *  tablero de Procesos dice lo mismo en la cabecera de cada columna: una
 *  etapa con dos descripciones son dos etapas.
 *
 *  `memberName` es null cuando la persona que atendía la etapa ya no está en el
 *  equipo: el id sobrevive a la baja, el nombre no. */
export function stageOwnerLabel(
  ownerType: StageOwnerType,
  teamPolicy: StageTeamPolicy | null,
  memberName: string | null,
  policyName: string | null = null,
): string {
  if (ownerType === 'bot') return 'El Agente'
  if (ownerType === 'team') {
    // Una política nombrada se nombra: es la razón de ser de la entidad.
    if (policyName !== null) return `El equipo, política ${policyName}`
    if (teamPolicy === 'balanced') return 'El equipo, equilibrada'
    // 'none' y null significan lo mismo: sin asignación automática.
    return 'El equipo, sin asignación automática'
  }
  return memberName ?? 'Una persona'
}

/** Why deleting this funnel is refused, in the tenant's words — or null when it
 *  can be deleted. Mirrors the API's guard order exactly, so the disabled
 *  button and the 409 never say different things. */
export function pipelineDeleteBlocker(p: PipelineView, total: number): string | null {
  if (total <= 1) return 'Es tu único proceso. Crea otro antes de eliminar este.'
  if (p.isDefault) return 'Es tu proceso predeterminado. Marca otro como predeterminado primero.'
  if (p.leadCount > 0) {
    return `Tiene ${p.leadCount} ${p.leadCount === 1 ? 'prospecto' : 'prospectos'}. Muévelos a otro proceso primero.`
  }
  return null
}

/** Why deleting this stage is refused, or null. Same order as the API's. */
export function stageDeleteBlocker(
  s: PipelineStageView,
  stages: PipelineStageView[],
): string | null {
  if (s.leadCount > 0) {
    return `Tiene ${s.leadCount} ${s.leadCount === 1 ? 'prospecto' : 'prospectos'}. Muévelos a otra etapa primero.`
  }
  if (s.kind === 'open' && stages.filter((x) => x.kind === 'open').length <= 1) {
    return 'Un proceso necesita al menos una etapa abierta.'
  }
  return null
}
